<script lang="ts">
  import { page } from '$app/stores';
  import { slide } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  import Banner from '$lib/components/website/Banner.svelte';
  import Footer from '$lib/components/website/Footer.svelte';
  import Nav from '$lib/components/website/nav/Nav.svelte';
  import type { PromotionalBannerProps } from '$lib/types/layoutTypes';
  import { setTheme } from '$lib/stores/themeStore';
  import CtaPanel from '$lib/components/website/ctaPanel/CtaPanel.svelte';
  import { PUBLIC_SEGMENT_KEY } from '$env/static/public';
  import { onMount } from 'svelte';
  import { setUser } from '$lib/stores/userStore.js';
  import { getMe } from '$lib/helpers/auth0Helpers.js';
  import {
    analytics,
    initSegment,
    checkoutStarted,
    trackCartUpdated,
    trackIdentifyUser,
    trackUserOnboarded,
    trackPaymentEntered,
  } from '$lib/gateway/segment.js';
  import { CONTENT_TYPES, HEADER_VARIANT } from '$lib/constants/constants';
  import { ensureOptimizely, getVariationItem, optimizelyStore } from '$lib/stores/optimizelyStore';
  import type { VariationContainerProps } from '$lib/types/contentTypes';
  import { initBraze } from '$lib/gateway/brazeClient';

  $: path = $page.url.pathname;
  $: isHomepage = $page.url.pathname === '/';

  export let data;
  let scrollY;
  let pathsWithoutLayout = ['/join', '/checkout'];

  const findPrioritaryPromotionalBanner = (
    siteBanner: PromotionalBannerProps | undefined,
    pageBanner: PromotionalBannerProps | undefined,
  ) => {
    if (!siteBanner?.priority && !pageBanner?.priority) return null;
    if (!siteBanner?.priority && pageBanner?.priority) return pageBanner;
    if (siteBanner?.priority && !pageBanner?.priority) return siteBanner;
    if (Number(pageBanner?.priority) < Number(siteBanner?.priority)) return pageBanner;
    if (Number(pageBanner?.priority) > Number(siteBanner?.priority)) return siteBanner;
    if (Number(pageBanner?.priority) == Number(siteBanner?.priority)) return siteBanner;
    return null;
  };

  const promotionalBannerHasVariationContainer =
    (data?.headerPromotionalBanner as VariationContainerProps<PromotionalBannerProps>)
      ?.contentType === CONTENT_TYPES.VARIATION_CONTAINER;

  let promotionalBannerVariationItem: VariationContainerProps<PromotionalBannerProps>['variationsCollection']['items'][0];

  onMount(async () => {
    const user = await getMe();
    if (user) setUser(user);
    else setUser(null);

    initSegment();

    analytics.page();

    (async () => {
      const braze = await initBraze();

      if (braze) {
        braze.logCustomEvent('sweat_web_paywall_page_view', {
          slug: window.location.pathname,
        });
      }
    })();

    // Check for segment evetns on paywall pages only
    if (path.includes('join')) {
      // Wait 15s to avoid undefined
      setTimeout(() => {
        analytics.track('Checkout Started', checkoutStarted());
        document.querySelectorAll("input[name='radio']").forEach((element) => {
          element.addEventListener('click', trackCartUpdated);
        });
        // Check for user signup
        document.getElementById('signup')?.addEventListener('submit', function () {
          // Wait 7s for canonicalId
          setTimeout(() => {
            trackIdentifyUser();
            trackUserOnboarded();
          }, 7000);
          // Wait 60s for address input to enable checkout submit button
          setTimeout(() => {
            if (document.querySelector("button[data-testid='checkout-submit']") != null) {
              trackPaymentEntered();
            }
          }, 60000);
        });
        // Check for user login
        if (document.querySelector("a[data-login='change-login']")) {
          // Wait 7s for canonicalId
          setTimeout(() => {
            trackIdentifyUser();
            trackUserOnboarded();
          }, 7000);
          // Wait 60s for address input to enable checkout submit button
          setTimeout(() => {
            if (document.querySelector("button[data-testid='checkout-submit']") != null) {
              trackPaymentEntered();
            }
          }, 60000);
        }
      }, 1500);
    }

    if (promotionalBannerHasVariationContainer && !isLayoutHidden) {
      if (!$optimizelyStore.isReady) {
        await ensureOptimizely();
      }

      const variationItem = getVariationItem(
        data?.headerPromotionalBanner as VariationContainerProps<PromotionalBannerProps>,
      );

      if (variationItem) {
        promotionalBannerVariationItem = variationItem;
      }
    }
  });

  $: sitePreferencePromotionalBanner = data?.headerPromotionalBanner;
  $: pagePromotionalBanner = $page?.data?.pageData?.promotionalBanner;
  $: prioritaryPromotionalBanner = findPrioritaryPromotionalBanner(
    promotionalBannerVariationItem || sitePreferencePromotionalBanner,
    pagePromotionalBanner,
  );

  $: setTheme(
    $page?.data?.pageData?.headerVariant || data?.headerVariant,
    data?.headerCtaBackground,
    data?.headerCtaTextColour,
    $page.url,
  );

  $: isTransparentHeaderVariant =
    $page?.data?.pageData?.headerVariant === HEADER_VARIANT.TRANSPARENT;

  $: isLayoutHidden = $page.error
    ? false
    : pathsWithoutLayout.some((path) => $page.url.pathname.startsWith(path));
</script>

<svelte:head>
  <title>{$page?.data?.pageData?.seoPageTitle} - Sweat</title>
  <meta name="description" content={$page?.data?.pageData?.seoPageDescription} />

  <meta property="og:type" content="website" />
  <meta property="og:typsite_name" content="SWEAT" />
  <meta property="og:title" content={$page?.data?.pageData?.seoPageTitle} />
  <meta property="og:description" content={$page?.data?.pageData?.seoPageDescription} />
  <meta property="og:url" content={$page.url} />

  <meta property="og:image" content={$page?.data?.pageData?.socialShareImage?.url} />
  <meta property="og:image:alt" content={$page?.data?.pageData?.socialShareImage?.title} />
  <meta property="og:image:width" content={$page?.data?.pageData?.socialShareImage?.width} />
  <meta property="og:image:height" content={$page?.data?.pageData?.socialShareImage?.height} />

  {#if isHomepage && prioritaryPromotionalBanner?.backgroundColour}
    <meta name="theme-color" content={prioritaryPromotionalBanner?.backgroundColour} />
  {/if}

  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content={$page?.data?.pageData?.seoPageTitle} />
  <meta name="twitter:description" content={$page?.data?.pageData?.seoPageDescription} />
  <meta name="SEGMENT_TOKEN" content={PUBLIC_SEGMENT_KEY} />

  {#if $page?.data?.pageData?.hidePageFromSearchEnginesNoindex}
    <meta name="robots" content="noindex" />
  {/if}
  <link rel="canonical" href={$page?.data?.pageData?.canonicalUrl || $page.url.href} />
  <!-- CookiePro Cookies Consent Notice start for sweat.com -->
  <script
    src="https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js"
    type="text/javascript"
    charset="UTF-8"
    data-domain-script="fb492ee1-7a9e-4a5d-a74b-860171e92d36"
  ></script>
  <script type="text/javascript">
    function OptanonWrapper() {}
  </script>
  <!-- CookiePro Cookies Consent Notice end for sweat.com -->
  <!-- Stripe -->
  <script src="https://js.stripe.com/v3/"></script>
  <!-- Plausible -->
  <script
    defer
    data-domain="sweat.com"
    src="https://plausible.io/js/script.tagged-events.js"
  ></script>
  <script>
    window.plausible ||
      function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
  </script>
  <!-- Add Segment's OneTrust Consent Wrapper -->
  <script
    src="https://cdn.jsdelivr.net/npm/@segment/analytics-consent-wrapper-onetrust@latest/dist/umd/analytics-onetrust.umd.js"
  ></script>
  <!-- Partnerize Lading Page tag -->
  {#if path.includes('/join') || path.includes('/checkout')}
    <script src="https://pzapi-ij.com/b/1101l751/1011l486.js" async></script>
  {/if}
  {#if path.includes('/checkout/success')}
    <!-- Partnerize Conversion Tracking tag -->
    <script src="https://pzapi-nb.com/b/1101l751/1101l383.js" async></script>
  {/if}
</svelte:head>
<main class="min-h-screen">
  {#if !isLayoutHidden}
    <div
      class="left-0 top-0 z-10 w-full {isTransparentHeaderVariant
        ? 'fixed '
        : 'sticky'} {isTransparentHeaderVariant && (!scrollY || scrollY === 0)
        ? '[&_nav:not(.menu-open)]:bg-[transparent] [&_nav:not(.menu-open)_.logo_path]:![fill:white] [&_nav:not(.menu-open)_.menu-button_path]:!stroke-[white]  [&_nav:not(.menu-open)_.nav-item]:!text-white'
        : ''}"
    >
      {#if !promotionalBannerHasVariationContainer || (promotionalBannerHasVariationContainer && $optimizelyStore.isReady)}
        {#if prioritaryPromotionalBanner?.message && (isTransparentHeaderVariant || (!isTransparentHeaderVariant && scrollY === 0))}
          <div transition:slide={{ delay: 500, duration: 400, easing: quintOut, axis: 'y' }}>
            <Banner
              backgroundColour={prioritaryPromotionalBanner?.backgroundColour}
              bannerText={prioritaryPromotionalBanner?.message}
              textColour={prioritaryPromotionalBanner?.textColour}
              linkText={prioritaryPromotionalBanner?.callToAction}
              linkUrl={prioritaryPromotionalBanner?.ctaUrl}
            />
          </div>
        {/if}
      {/if}
      <Nav
        headerNavigation={data?.headerNavigation}
        headerCta={data.headerCta}
        headerCtaBackground={data?.headerCtaBackground}
        headerCtaTextColour={data?.headerCtaTextColour}
      />
    </div>
  {/if}
  <slot />
  {#if data.footer?.footerCtaPanel && !(path.includes('/join') || path.includes('/checkout') || path.includes('partner'))}
    <div class="mt-12 w-full lg:mt-20">
      <CtaPanel ctaPanel={data.footer?.footerCtaPanel} />
    </div>
  {/if}
  <Footer
    {isLayoutHidden}
    navigationColumns={data.footer?.navigationColumns}
    socialLinks={data.footer?.socialLinks}
  />
</main>

<svelte:window bind:scrollY />

<style global>
  @import '../assets/styles/app.css';
</style>
